/** Build JSON body for POST/PUT settlement group API from local form state. */

import {
  masterApiErrorMessage,
  translateAggregateBackendMessage,
} from '../../../main/utils/masterApiError';

function trimOrEmpty(v) {
  return v == null ? '' : String(v).trim();
}

function intOrDefault(v, def) {
  const n = parseInt(String(v).trim(), 10);
  return Number.isFinite(n) ? n : def;
}

export function emptySettlementGroupForm() {
  return {
    code: '',
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    separate_checkout: false,
    active: true,
    sort_order: 0,
  };
}

export function buildSettlementGroupPayload(form) {
  return {
    code: trimOrEmpty(form.code),
    name: trimOrEmpty(form.name),
    name_ar: trimOrEmpty(form.name_ar) || null,
    description: trimOrEmpty(form.description) || null,
    description_ar: trimOrEmpty(form.description_ar) || null,
    separate_checkout: Boolean(form.separate_checkout),
    active: Boolean(form.active),
    sort_order: intOrDefault(form.sort_order, 0),
  };
}

export function settlementGroupFromApi(row) {
  if (!row || typeof row !== 'object') return emptySettlementGroupForm();
  return {
    code: row.code ?? '',
    name: row.name ?? '',
    name_ar: row.name_ar ?? '',
    description: row.description ?? '',
    description_ar: row.description_ar ?? '',
    separate_checkout: Boolean(row.separate_checkout),
    active: row.active !== false,
    sort_order: row.sort_order ?? 0,
  };
}

/** Seeded master row — UI must not allow deactivate/delete. */
export function isDefaultSettlementGroup(formOrRow) {
  if (!formOrRow) return false;
  return String(formOrRow.code || '').trim().toLowerCase() === 'default';
}

const CODE_MAX_LEN = 120;
const NAME_MAX_LEN = 200;

export function validateSettlementGroupClient(form, t, opts = {}) {
  const { isCreate = false } = opts;
  const errors = {};
  const code = trimOrEmpty(form.code);
  if (!code) {
    errors.code = t('settlementGroups.validationCodeRequired');
  } else if (code.length > CODE_MAX_LEN) {
    errors.code = t('settlementGroups.validationCodeMaxLength', { max: CODE_MAX_LEN });
  } else if (isCreate && !/^[a-z0-9_]+$/.test(code)) {
    errors.code = t('settlementGroups.validationCodeSlug');
  }

  const name = trimOrEmpty(form.name);
  if (!name) {
    errors.name = t('settlementGroups.validationNameRequired');
  } else if (name.length > NAME_MAX_LEN) {
    errors.name = t('settlementGroups.validationNameMaxLength', { max: NAME_MAX_LEN });
  }

  return errors;
}

const GROUP_API_TOPLEVEL_KEYS = new Set([
  'detail',
  'non_field_errors',
  'message_key',
  'message',
  'error',
]);

function joinDrfMessages(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val.trim();
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && typeof item.message === 'string') return item.message;
        return '';
      })
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

export function settlementGroupApiValidationFeedback(err, t) {
  const fieldErrors = {};
  let banner = '';

  const data = err && err.response && err.response.data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
      banner = translateAggregateBackendMessage(
        data.non_field_errors.map(String).join('; '),
        t
      );
    }

    Object.keys(data).forEach((key) => {
      if (GROUP_API_TOPLEVEL_KEYS.has(key)) return;
      const joined = joinDrfMessages(data[key]);
      if (joined) {
        fieldErrors[key] = translateAggregateBackendMessage(joined, t);
      }
    });
  }

  if (!banner && Object.keys(fieldErrors).length === 0) {
    banner = masterApiErrorMessage(err, t);
  }

  return { fieldErrors, saveError: banner };
}
